import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import axios from 'axios';
import { Briefcase, Star, MapPin, ShieldCheck, ArrowRight, Loader2 } from 'lucide-react';
import { useSEO, buildBreadcrumbSchema, buildFaqSchema } from '../utils/seo';
import Header from '../components/Header';
import PublicTrustStrip from '../components/PublicTrustStrip';
import PulseReportCTA from '../components/PulseReportCTA';
import StickyMobileCTA from '../components/StickyMobileCTA';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const BASE_URL = 'https://shopperden.com';

const COUNTRY_OF_CITY = {
  // Quick lookup so the route /pros/hyderabad works without a country prefix.
  // Falls back to "IN" since most demand is currently India-side.
  london: 'UK', manchester: 'UK', birmingham: 'UK', leeds: 'UK', liverpool: 'UK',
  bristol: 'UK', edinburgh: 'UK', glasgow: 'UK', sheffield: 'UK',
  toronto: 'CA', vancouver: 'CA', montreal: 'CA', mississauga: 'CA', brampton: 'CA',
  hamilton: 'CA', ottawa: 'CA',
};

const titleCase = (s) => (s || '')
  .split('-')
  .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
  .join(' ');


/**
 * Public city directory — /pros/{city-slug}.
 *
 * SEO goal: when a Hyderabad solicitor Googles "best property solicitor
 * Hyderabad" we want this page to surface (their existing Google reviews
 * appear here verbatim with a "Claim my profile" CTA). Pure organic
 * claim funnel — no ad spend.
 */
const CityProsDirectoryPage = () => {
  const { city: citySlug } = useParams();
  const cityLabel = titleCase(citySlug);
  const country = COUNTRY_OF_CITY[citySlug?.toLowerCase()] || 'IN';

  const [items, setItems] = useState([]);
  const [unlockFee, setUnlockFee] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams({
          city: cityLabel,
          country,
          limit: '25',
        });
        const res = await axios.get(`${API_URL}/api/pro-leads?${params}`);
        if (!cancelled) {
          setItems(res.data?.items || []);
          setUnlockFee(res.data?.unlock_fee);
        }
      } catch { /* silent */ }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [citySlug, cityLabel, country]);

  const canonical = `${BASE_URL}/pros/${citySlug?.toLowerCase()}`;
  const description = `Verified estate agents, conveyancing solicitors, mortgage brokers and property managers in ${cityLabel}. Independent listings with real Google ratings — no agency markups. Connection fee ${unlockFee ? `${unlockFee.currency === 'inr' ? '₹' : unlockFee.currency === 'cad' ? 'C$' : '£'}${unlockFee.amount}` : '£15'}.`;

  useSEO({
    title: `Best property professionals in ${cityLabel} — Estate agents, solicitors & brokers`,
    description,
    canonical,
    type: 'website',
    schema: [
      buildBreadcrumbSchema([
        { name: 'Home', url: '/' },
        { name: 'Local Pros', url: '/find-a-professional' },
        { name: cityLabel, url: `/pros/${citySlug?.toLowerCase()}` },
      ]),
      buildFaqSchema([
        {
          q: `Are these professionals in ${cityLabel} verified?`,
          a: `Every listing is sourced directly from Google Places with their public rating and review count. Each professional is invited to claim their profile and confirm credentials before any buyer can unlock direct contact.`,
        },
        {
          q: `How much is the connection fee?`,
          a: `${unlockFee ? `${unlockFee.currency === 'inr' ? '₹' : unlockFee.currency === 'cad' ? 'C$' : '£'}${unlockFee.amount}` : '£15.52'} per pro contact unlock. Same flat fee as our property Vault Unlock — no per-percentage commission.`,
        },
        {
          q: `Are you ${cityLabel}'s {first_pro}?`.replace('{first_pro}', items[0]?.name || 'a local professional'),
          a: `If so, your profile is already live on Shopperden. Click "Claim my profile" on your listing card to verify your details and start earning 20% commission on every referred client.`,
        },
      ]),
      // ItemList schema so Google can show a "Top {N} in {City}" rich list.
      {
        '@context': 'https://schema.org',
        '@type': 'ItemList',
        name: `Verified property professionals in ${cityLabel}`,
        itemListElement: (items || []).slice(0, 10).map((it, i) => ({
          '@type': 'ListItem',
          position: i + 1,
          item: {
            '@type': 'LocalBusiness',
            name: it.name,
            address: { '@type': 'PostalAddress', addressLocality: cityLabel, addressCountry: country },
            aggregateRating: it.rating ? { '@type': 'AggregateRating', ratingValue: it.rating, reviewCount: it.review_count || 0 } : undefined,
          },
        })),
      },
    ],
  });

  // Group by category for nicer scanability
  const byCategory = (items || []).reduce((acc, it) => {
    const k = (it.category || 'Other').toLowerCase();
    (acc[k] = acc[k] || []).push(it);
    return acc;
  }, {});
  const CATEGORY_ORDER = ['estate agents', 'real estate solicitors', 'mortgage brokers', 'property managers'];

  return (
    <div className="min-h-screen bg-white" data-testid={`city-pros-${citySlug}`}>
      <Header />
      <PublicTrustStrip />

      <section className="bg-gradient-to-b from-[#FEF8EC] via-white to-white border-b border-slate-100">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-12">
          <p className="text-[10px] tracking-[0.3em] text-[#E07A3A] uppercase font-bold mb-3">
            Verified by Google · {country}
          </p>
          <h1 className="font-['Playfair_Display'] text-3xl sm:text-5xl text-[#0F172A] leading-tight" data-testid="city-pros-h1">
            Best property professionals in {cityLabel}
          </h1>
          <p className="text-sm sm:text-base text-slate-600 mt-3 max-w-2xl">
            Estate agents, conveyancing solicitors, mortgage brokers and property managers — independently rated, no agency markup.
          </p>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-4 sm:px-6 py-10">
        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
          </div>
        ) : items.length === 0 ? (
          <div className="text-center py-12 text-slate-500" data-testid="city-pros-empty">
            <p>No professionals indexed yet for {cityLabel}. Our harvester picks up new cities every rotation.</p>
            <Link to="/find-a-professional" className="text-[#E07A3A] underline text-sm mt-3 inline-block">Browse other cities</Link>
          </div>
        ) : (
          CATEGORY_ORDER.filter((c) => byCategory[c]?.length).map((catKey) => (
            <div key={catKey} className="mb-10" data-testid={`city-pros-cat-${catKey.replace(/\s/g, '-')}`}>
              <h2 className="font-['Playfair_Display'] text-xl sm:text-2xl text-[#0F172A] mb-4 capitalize">
                {catKey} in {cityLabel}
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {byCategory[catKey].map((it) => (
                  <article
                    key={it.place_id}
                    className="border border-slate-200 rounded p-4 bg-white hover:shadow-md transition-shadow"
                    data-testid={`city-pro-card-${it.place_id}`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <p className="text-[10px] tracking-[0.2em] text-[#E07A3A] uppercase font-semibold mb-1">
                          {(it.category || '').replace(/\b\w/g, (c) => c.toUpperCase())}
                        </p>
                        <h3 className="font-semibold text-[#0F172A] truncate">{it.name}</h3>
                        <p className="text-xs text-slate-500 mt-1 flex items-center gap-1">
                          <MapPin className="h-3 w-3" />
                          {it.city}, {it.country}
                        </p>
                      </div>
                      {it.rating && (
                        <div className="flex flex-col items-end flex-shrink-0">
                          <div className="flex items-center gap-1 text-amber-600">
                            <Star className="h-3.5 w-3.5 fill-current" />
                            <span className="text-sm font-bold">{Number(it.rating).toFixed(1)}</span>
                          </div>
                          <span className="text-[10px] text-slate-400">{it.review_count || 0} reviews</span>
                        </div>
                      )}
                    </div>
                    <div className="mt-3 pt-3 border-t border-slate-100 flex items-center justify-between gap-2">
                      <Link
                        to={`/claim/${it.place_id}`}
                        className="text-xs font-semibold text-[#E07A3A] hover:text-[#C4652E] inline-flex items-center gap-1"
                        data-testid={`city-pro-claim-${it.place_id}`}
                      >
                        Is this you? Claim profile <ArrowRight className="h-3 w-3" />
                      </Link>
                      <Link
                        to={`/auth?role=buyer`}
                        className="text-[10px] uppercase tracking-wider font-bold text-slate-500 hover:text-[#0F172A]"
                      >
                        Unlock
                      </Link>
                    </div>
                  </article>
                ))}
              </div>
            </div>
          ))
        )}
      </section>

      <PulseReportCTA role="buyer" city={cityLabel} country={country} dataTestId={`pulse-cta-city-${citySlug}`} />

      <section className="bg-slate-50 border-t border-slate-100">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-12 text-center">
          <ShieldCheck className="h-8 w-8 text-[#E07A3A] mx-auto mb-3" />
          <h2 className="font-['Playfair_Display'] text-2xl text-[#0F172A] mb-2">Are you a {cityLabel} professional?</h2>
          <p className="text-sm text-slate-600 mb-5 max-w-md mx-auto">
            Claim your free Featured Pro profile. Buyers pay our flat connection fee to reach you — no ad spend.
            Earn 20% commission on every referred client.
          </p>
          <Link
            to="/for/professionals"
            className="inline-flex items-center gap-2 bg-[#0F172A] hover:bg-slate-800 text-white text-sm tracking-[0.2em] font-bold uppercase px-6 py-3"
            data-testid="city-pros-join-cta"
          >
            <Briefcase className="h-4 w-4" />
            How it works for pros
          </Link>
        </div>
      </section>

      <StickyMobileCTA
        label={`Unlock verified pros in ${cityLabel}`}
        ctaText="Browse"
        to="/auth?role=buyer"
        dataTestId={`sticky-cta-city-${citySlug}`}
      />
    </div>
  );
};

export default CityProsDirectoryPage;
